import React, { FC, useState } from "react"
import { KSpan } from "kahuna-base-react-components"
import { AdvanceDealAdvance } from "../../../../../types"
import { STATUS_STYLE } from "./dealStatus"

const GREEN = "#059669"
// Deeper shade of the artist bar's lavender, so a filled marker reads clearly against it.
const ARTIST = "#7C5CD6"
const TEXT_MUTED = "#71717a"
const TEXT_PRIMARY = "#111"
const HAIRLINE = "#f4f4f5"

const MONTH_WIDTH = 60
const LABEL_WIDTH = 224
const ROW_HEIGHT = 60
const BAR_HEIGHT = 14
const MARKER_SIZE = 20

// Backend months are "YYYYMM"; a month index makes the arithmetic trivial.
const toIndex = (key: string | null | undefined): number | null => {
  if (!key || key.length < 6) return null
  const year = parseInt(key.slice(0, 4), 10)
  const month = parseInt(key.slice(4, 6), 10)
  return isNaN(year) || isNaN(month) ? null : year * 12 + (month - 1)
}

const indexLabel = (index: number, withYear = false) =>
  new Date(Math.floor(index / 12), index % 12, 1).toLocaleDateString("en-US", {
    month: "short",
    year: withYear ? "numeric" : undefined
  })

const usd0 = (value: number) => `$${Math.round(value).toLocaleString("en-US")}`

type SegmentKind = "waiting" | "company" | "companyProjected" | "artist" | "artistProjected" | "noProjection"

// A bar between two month lines: it starts on the line of month `from` and ends on the line of
// month `to`, so bar ends meet the event markers, which also sit on month lines.
type Segment = { kind: SegmentKind; from: number; to: number; title: string }

const SEGMENT_STYLE: { [kind in SegmentKind]: React.CSSProperties } = {
  waiting: { background: "#E8EBF0" },
  company: { background: GREEN },
  // Projected bars carry a fine diagonal dash fill in the actual bar's colour, plus a dashed outline.
  companyProjected: {
    background: `repeating-linear-gradient(135deg, rgba(5, 150, 105, 0.55) 0 2px, #ECFDF5 2px 6px)`,
    border: "1px dashed #34D399"
  },
  artist: { background: "#B9A9E6" },
  artistProjected: {
    background: `repeating-linear-gradient(135deg, rgba(167, 139, 250, 0.85) 0 2px, #F7F4FF 2px 6px)`,
    border: "1px dashed #C4B5FD"
  },
  noProjection: { background: "transparent", border: "1px dashed #CBD5E1" }
}

const range = (from: number, to: number) => `${indexLabel(from, true)} to ${indexLabel(to, true)}`

// Splits one advance into back-to-back bars, earliest first. Zero-length bars are dropped; the
// event markers still show where things happened.
const buildSegments = (advance: AdvanceDealAdvance, todayIndex: number): Segment[] => {
  const deployment = toIndex(advance.deploymentMonth)
  if (deployment === null) return []
  const start = Math.max(
    toIndex(advance.actualStartMonth) ?? toIndex(advance.expectedStartMonth) ?? deployment,
    deployment
  )
  const companyEnd = toIndex(advance.companyRecoupMonth)
  const artistEnd = toIndex(advance.artistRecoupMonth)
  const segments: Segment[] = []
  const push = (kind: SegmentKind, from: number, to: number, title: string) => {
    if (to > from) segments.push({ kind, from, to, title })
  }

  push("waiting", deployment, start, `Waiting · ${range(deployment, start)}`)

  if (companyEnd === null) {
    // Nothing is projected, so show what has been collected so far and an open end.
    const collectedTo = Math.max(todayIndex, start)
    push("company", start, collectedTo, `Company recoup · ${range(start, collectedTo)} (no projected recoup)`)
    push("noProjection", collectedTo, collectedTo + 3, "No projected recoup")
    return segments
  }

  const end = Math.max(companyEnd, start)
  if (advance.isActual || end <= todayIndex) {
    push("company", start, end, `Company recouped · ${range(start, end)}`)
  } else {
    const actualTo = Math.min(Math.max(todayIndex, start), end)
    push("company", start, actualTo, `Company recoup, actual · ${range(start, actualTo)}`)
    push("companyProjected", actualTo, end, `Company recoup, projected · ${range(actualTo, end)}`)
  }

  if (artistEnd !== null && artistEnd > end) {
    if (advance.artistIsActual || artistEnd <= todayIndex) {
      push("artist", end, artistEnd, `Artist recouped · ${range(end, artistEnd)}`)
    } else {
      // Not cleared yet: what has passed is progress so far, the rest is the projection.
      const actualTo = Math.min(Math.max(todayIndex, end), artistEnd)
      push("artist", end, actualTo, `Artist recoup, actual · ${range(end, actualTo)}`)
      push("artistProjected", actualTo, artistEnd, `Artist recoup, projected · ${range(actualTo, artistEnd)}`)
    }
  }
  return segments
}

type EventKind = "deployed" | "start" | "company" | "artist"

type TimelineEvent = { kind: EventKind; index: number; actual: boolean; title: string; detail: string }

const EVENT_STYLE: { [kind in EventKind]: { color: string; glyph: string } } = {
  deployed: { color: "#4F46E5", glyph: "$" },
  start: { color: "#D97706", glyph: "P" },
  company: { color: GREEN, glyph: "C" },
  artist: { color: ARTIST, glyph: "A" }
}

const buildEvents = (advance: AdvanceDealAdvance): TimelineEvent[] => {
  const events: TimelineEvent[] = []
  const deployed = toIndex(advance.deploymentMonth)
  if (deployed !== null) {
    events.push({
      kind: "deployed",
      index: deployed,
      actual: true,
      title: "Advance deployed",
      detail: `${usd0(advance.amount)} · ${indexLabel(deployed, true)}`
    })
  }
  // Principal recovery starts with the first cash applied to the company balance; before that
  // only the expected month is known.
  const actualStart = toIndex(advance.actualStartMonth)
  const expectedStart = toIndex(advance.expectedStartMonth)
  const started = actualStart ?? expectedStart
  if (started !== null) {
    events.push({
      kind: "start",
      index: started,
      actual: actualStart !== null,
      title: actualStart !== null ? "Principal recovery started" : "Principal recovery expected to start",
      detail: indexLabel(started, true)
    })
  }
  const company = toIndex(advance.companyRecoupMonth)
  if (company !== null) {
    events.push({
      kind: "company",
      index: company,
      actual: advance.isActual,
      title: advance.isActual ? "Company recouped" : "Company recoup projected",
      detail: indexLabel(company, true)
    })
  }
  const artist = toIndex(advance.artistRecoupMonth)
  if (artist !== null) {
    events.push({
      kind: "artist",
      index: artist,
      actual: advance.artistIsActual,
      title: advance.artistIsActual ? "Artist recouped" : "Artist recoup projected",
      detail: indexLabel(artist, true)
    })
  }
  return events
}

const EventMarker: FC<{ kind: EventKind; actual: boolean; size?: number }> = ({ kind, actual, size = MARKER_SIZE }) => {
  const { color, glyph } = EVENT_STYLE[kind]
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.5,
        fontWeight: 700,
        lineHeight: 1,
        color: actual ? "#fff" : color,
        background: actual ? color : "#fff",
        border: actual ? "2px solid #fff" : `1.5px dashed ${color}`,
        boxShadow: actual ? "0 1px 3px rgba(0,0,0,0.25)" : "none",
        boxSizing: "border-box"
      }}
    >
      {glyph}
    </span>
  )
}

type Hover = { x: number; y: number; title: string; lines: string[] }

// Small status chip beside the advance name, in the page's shared status colours.
const AdvanceStatus: FC<{ status: string }> = ({ status }) => {
  const style = STATUS_STYLE[status] || { label: status, bg: "#f1f5f9", color: TEXT_MUTED }
  return (
    <span
      style={{
        backgroundColor: style.bg,
        color: style.color,
        padding: "1px 8px",
        borderRadius: 999,
        fontSize: 10.5,
        fontWeight: 600,
        lineHeight: "16px",
        whiteSpace: "nowrap"
      }}
    >
      {style.label}
    </span>
  )
}

const LegendItem: FC<{ swatch: React.ReactNode; text: string }> = ({ swatch, text }) => (
  <span className="inline-flex items-center gap-2">
    {swatch}
    <KSpan text={text} fontSize={12} color={TEXT_MUTED} lineHeight="16px" />
  </span>
)

const BarSwatch: FC<{ kind: SegmentKind }> = ({ kind }) => (
  <span
    style={{
      width: 26,
      height: 10,
      borderRadius: 4,
      display: "inline-block",
      boxSizing: "border-box",
      ...SEGMENT_STYLE[kind]
    }}
  />
)

const AdvanceRecoupmentTimeline: FC<{ advances: AdvanceDealAdvance[] }> = ({ advances }) => {
  const [hover, setHover] = useState<Hover | null>(null)
  const now = new Date()
  const todayIndex = now.getFullYear() * 12 + now.getMonth()

  const rows = advances.map((advance) => ({
    advance,
    segments: buildSegments(advance, todayIndex),
    events: buildEvents(advance)
  }))
  const edges: number[] = [todayIndex]
  rows.forEach(({ advance, segments, events }) => {
    segments.forEach((segment) => edges.push(segment.from, segment.to))
    events.forEach((event) => edges.push(event.index))
    const target = toIndex(advance.targetDate)
    if (target !== null) edges.push(target)
  })

  if (rows.every((row) => row.segments.length === 0)) {
    return <KSpan text="There are no advances to chart." fontSize={13} color={TEXT_MUTED} />
  }

  // One empty month ahead of the first event, so its marker is not cut off by the label column.
  const first = Math.min(...edges) - 1
  const last = Math.max(...edges) + 1
  const months = Array.from({ length: last - first + 1 }, (_, i) => first + i)
  const trackWidth = months.length * MONTH_WIDTH
  const x = (index: number) => (index - first) * MONTH_WIDTH
  // The vertical line of a month: the middle of its column, directly under its axis label.
  const line = (index: number) => x(index) + MONTH_WIDTH / 2
  const show = (event: React.MouseEvent, title: string, lines: string[]) =>
    setHover({ x: event.clientX, y: event.clientY, title, lines })

  return (
    <div className="flex flex-col gap-4">
      {/* Thin, rounded scrollbar (WebKit/Blink); the track stays invisible. */}
      <div
        style={{ overflowX: "auto" }}
        className="[&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300"
      >
        <div style={{ width: LABEL_WIDTH + trackWidth, minWidth: "100%" }}>
          {/* Month axis: year labels above, today as a dark chip */}
          <div className="flex">
            <div
              style={{ width: LABEL_WIDTH, flexShrink: 0, position: "sticky", left: 0, background: "#fff", zIndex: 3 }}
            />
            <div className="flex" style={{ width: trackWidth }}>
              {months.map((month) => (
                <div key={month} className="flex flex-col items-center gap-1 pb-2" style={{ width: MONTH_WIDTH }}>
                  <KSpan
                    text={month % 12 === 0 || month === first ? String(Math.floor(month / 12)) : "\u00a0"}
                    fontSize={10.5}
                    fontWeight={600}
                    color="#a1a1aa"
                    letterSpacing="0.06em"
                    lineHeight="14px"
                  />
                  {/* Plain span: the library's span keeps its letter spacing after the last letter, which
                      pulled the text off-centre inside the chip. The left padding balances it. */}
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "2px 9px",
                      borderRadius: 999,
                      background: month === todayIndex ? "#E5E7EB" : "transparent",
                      fontSize: 10.5,
                      fontWeight: 600,
                      lineHeight: "16px",
                      letterSpacing: "0.06em",
                      paddingLeft: "calc(9px + 0.06em)",
                      color: month === todayIndex ? "#374151" : TEXT_MUTED
                    }}
                  >
                    {indexLabel(month).toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {rows.map(({ advance, segments, events }) => {
            const target = toIndex(advance.targetDate)
            // Events that land in the same month fan out sideways instead of stacking.
            const perMonth: { [index: number]: number } = {}
            events.forEach((event) => (perMonth[event.index] = (perMonth[event.index] || 0) + 1))
            const placed: { [index: number]: number } = {}
            return (
              <div
                key={advance.advanceId}
                className="flex"
                style={{ height: ROW_HEIGHT, borderTop: `1px solid ${HAIRLINE}` }}
              >
                <div
                  className="flex flex-col justify-center pr-3"
                  style={{
                    width: LABEL_WIDTH,
                    flexShrink: 0,
                    position: "sticky",
                    left: 0,
                    background: "#fff",
                    zIndex: 3
                  }}
                >
                  <div className="flex items-center gap-2">
                    <KSpan
                      text={`Advance #${advance.advanceNumber}`}
                      fontSize={13}
                      fontWeight={600}
                      color={TEXT_PRIMARY}
                      lineHeight="18px"
                    />
                    <AdvanceStatus status={advance.performanceStatus} />
                  </div>
                  <KSpan
                    text={`${usd0(advance.amount)} · ${indexLabel(toIndex(advance.deploymentMonth) ?? first, true)}`}
                    fontSize={11.5}
                    color={TEXT_MUTED}
                    lineHeight="16px"
                  />
                </div>
                <div
                  className="relative"
                  style={{
                    width: trackWidth,
                    backgroundImage: `linear-gradient(to right, transparent calc(50% - 0.5px), ${HAIRLINE} calc(50% - 0.5px), ${HAIRLINE} calc(50% + 0.5px), transparent calc(50% + 0.5px))`,
                    backgroundSize: `${MONTH_WIDTH}px 100%`
                  }}
                >
                  {/* Today */}
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: line(todayIndex),
                      borderLeft: "1px dashed #9CA3AF"
                    }}
                  />
                  {segments.map((segment, segmentIndex) => {
                    // Bars run edge to edge so their ends meet the event markers; neighbouring
                    // segments keep a 2px gap between them.
                    const gapStart = segmentIndex === 0 ? 0 : 1
                    const gapEnd = segmentIndex === segments.length - 1 ? 0 : 1
                    return (
                      <div
                        key={`${segment.kind}-${segment.from}`}
                        onMouseMove={(event) =>
                          show(event, segment.title, [
                            `Advance #${advance.advanceNumber} · ${usd0(advance.amount)}`,
                            `Outstanding ${usd0(advance.companyOutstanding)} company · ${usd0(
                              advance.artistOutstanding
                            )} artist`
                          ])
                        }
                        onMouseLeave={() => setHover(null)}
                        style={{
                          position: "absolute",
                          top: (ROW_HEIGHT - BAR_HEIGHT) / 2,
                          height: BAR_HEIGHT,
                          left: line(segment.from) + gapStart,
                          width: (segment.to - segment.from) * MONTH_WIDTH - gapStart - gapEnd,
                          borderRadius: 6,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxSizing: "border-box",
                          ...SEGMENT_STYLE[segment.kind]
                        }}
                      >
                        {segment.kind === "noProjection" ? (
                          <KSpan text="No projection" fontSize={10} color="#94A3B8" lineHeight="12px" />
                        ) : null}
                      </div>
                    )
                  })}
                  {/* Target date */}
                  {target !== null ? (
                    <div
                      onMouseMove={(event) => show(event, "Target recoup", [indexLabel(target, true)])}
                      onMouseLeave={() => setHover(null)}
                      style={{
                        position: "absolute",
                        top: 8,
                        bottom: 8,
                        left: line(target) - 2,
                        width: 5,
                        display: "flex",
                        justifyContent: "center",
                        cursor: "default",
                        zIndex: 1
                      }}
                    >
                      <div style={{ borderLeft: "2px solid #7c3aed", height: "100%" }} />
                    </div>
                  ) : null}
                  {/* Events */}
                  {events.map((event) => {
                    const count = perMonth[event.index]
                    const slot = placed[event.index] || 0
                    placed[event.index] = slot + 1
                    const offset = (slot - (count - 1) / 2) * (MARKER_SIZE + 2)
                    return (
                      <div
                        key={event.kind}
                        onMouseMove={(e) => show(e, event.title, [event.detail, `Advance #${advance.advanceNumber}`])}
                        onMouseLeave={() => setHover(null)}
                        style={{
                          position: "absolute",
                          top: (ROW_HEIGHT - MARKER_SIZE) / 2,
                          display: "flex",
                          left: line(event.index) - MARKER_SIZE / 2 + offset,
                          zIndex: 2
                        }}
                      >
                        <EventMarker kind={event.kind} actual={event.actual} />
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-2 pt-1">
        <LegendItem swatch={<BarSwatch kind="company" />} text="Company recoup" />
        <LegendItem swatch={<BarSwatch kind="companyProjected" />} text="Company · projected" />
        <LegendItem swatch={<BarSwatch kind="artist" />} text="Artist recoup" />
        <LegendItem swatch={<BarSwatch kind="artistProjected" />} text="Artist · projected" />
        <LegendItem swatch={<BarSwatch kind="waiting" />} text="Waiting" />
        <LegendItem swatch={<EventMarker kind="deployed" actual size={16} />} text="Deployed" />
        <LegendItem swatch={<EventMarker kind="start" actual size={16} />} text="Principal recovery starts" />
        <LegendItem swatch={<EventMarker kind="company" actual size={16} />} text="Company recouped" />
        <LegendItem swatch={<EventMarker kind="artist" actual size={16} />} text="Artist recouped" />
        <LegendItem swatch={<EventMarker kind="company" actual={false} size={16} />} text="Projected event" />
        <LegendItem
          swatch={<span style={{ height: 14, borderLeft: "2px solid #7c3aed", display: "inline-block" }} />}
          text="Target"
        />
      </div>

      {hover ? (
        <div
          style={{
            position: "fixed",
            left: hover.x + 14,
            top: hover.y + 14,
            zIndex: 50,
            pointerEvents: "none",
            background: "#fff",
            border: "1px solid #e4e4e7",
            borderRadius: 10,
            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.12)",
            padding: "8px 12px",
            maxWidth: 300
          }}
        >
          <KSpan text={hover.title} fontSize={12.5} fontWeight={600} color={TEXT_PRIMARY} lineHeight="18px" />
          {hover.lines.map((line) => (
            <div key={line}>
              <KSpan text={line} fontSize={11.5} color={TEXT_MUTED} lineHeight="16px" />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default AdvanceRecoupmentTimeline
